/**
 * The per-row kebab (`⋮`) the list frame carries, and the document header's secondary actions on a
 * phone.
 *
 * Rows in the DeCA list are click-through to the document, so every action here
 * has to stop propagation: opening the menu, and choosing from it, must not also
 * navigate away from the row the user was acting on.
 *
 * **Fixed to the viewport, not to the cell (#111).** The list's `.table-wrap` scrolls sideways, and
 * `overflow-x: auto` turns the other axis into a scroller as well, so a menu positioned inside a cell
 * was cut off on the bottom rows at every width. The menu is `position: fixed`, placed from the
 * kebab's rect, and flips up when there is no room below. It stays in DOM order, inside the kebab's
 * wrapper: Tab still reaches it straight after the kebab, and the outside-press dismissal still
 * counts a press on an item as inside. A fixed menu does not follow what scrolls under it, so any
 * scroll outside the menu, and any resize, closes it. That depends on nothing around the list having
 * a `transform`, `filter` or `contain`, which would become the menu's containing block (design §2).
 *
 * **Focus.** Opening moves focus to the first item; Escape and choosing an item put it back on the
 * kebab. The item is focused back *before* its action runs, because the void dialog records the
 * focused element on open as the place to return to — and a chosen item has just been unmounted.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Icon, type IconName } from './Icon.tsx'
import { IconButton } from './primitives.tsx'
import { useDismissOnOutsideClick } from './useDismiss.ts'

export interface RowMenuItem {
  key: string
  label: string
  icon?: IconName
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}

/** The menu's distance from the kebab, and from every edge of the viewport. */
const GAP = 8

interface Placement {
  top: number
  right: number
}

export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<Placement | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Stable, so each row's document listeners are not re-subscribed on every render.
  const close = useCallback(() => {
    setOpen(false)
    setPlace(null)
  }, [])
  useDismissOnOutsideClick(ref, close)

  // The kebab is the wrapper's first child; `IconButton` takes no ref.
  const focusTrigger = () => ref.current?.querySelector<HTMLElement>(':scope > button')?.focus()

  useLayoutEffect(() => {
    const menu = menuRef.current
    if (!open || !menu || !ref.current) return
    if (!place) {
      // The viewport without its scrollbars, which is what `position: fixed` is measured against.
      const { clientWidth, clientHeight } = document.documentElement
      const anchor = ref.current.getBoundingClientRect()
      const height = menu.offsetHeight
      const below = anchor.bottom + GAP
      setPlace({
        top: below + height <= clientHeight - GAP ? below : Math.max(GAP, anchor.top - GAP - height),
        // Right-aligned to the kebab, unless that would push the menu's left edge off the screen.
        right: Math.max(GAP, Math.min(clientWidth - anchor.right, clientWidth - GAP - menu.offsetWidth)),
      })
      return
    }
    // Placed: now the first item can take focus. Not before, and never with a scroll — focusing a
    // menu that still sat inside `.table-wrap` would scroll it, and the scroll would close the menu.
    menu.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true })
  }, [open, place])

  useEffect(() => {
    if (!open) return
    function onScroll(event: Event) {
      // Capture catches every scroller's scroll; the menu's own, when it is taller than the screen,
      // is the one that must not close it.
      // (The page's own scroll targets the document, or the window itself, which is not a Node.)
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return
      close()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open, close])

  const usable = items.filter((item) => !item.disabled)
  if (usable.length === 0) return null

  return (
    <div
      className="row-menu"
      ref={ref}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return
        // On an open menu this Escape is the menu's: it stops here, so the drawer's listener on
        // `document` (bubble phase, `AppShell.tsx`) never sees it. The portal's `Dialog` listens in
        // the capture phase and would close first; no row menu sits in a dialog today (#166).
        event.stopPropagation()
        close()
        focusTrigger()
      }}
    >
      <IconButton
        icon="more"
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      />
      {open ? (
        <div
          ref={menuRef}
          className="menu menu--fixed"
          role="menu"
          // Measured hidden on the first pass, then shown where it fits.
          style={place ? { top: place.top, right: place.right } : { top: 0, right: 0, visibility: 'hidden' }}
        >
          {usable.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={item.danger ? 'menu__item menu__item--danger' : 'menu__item'}
              onClick={() => {
                focusTrigger()
                close()
                item.onSelect()
              }}
            >
              {item.icon ? <Icon name={item.icon} size={16} /> : null}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
